import React from "react";
import * as XLSX from "xlsx";
import {saveAs} from 'file-saver';

const RUBS_XLS_HEADER_ROW = [
  'Lease','Date','Amount','Account','Comments',
  'Payment Type','Payment Ref No','Payment Comments',
  'Payment Account','Portfolio\\Building\\Unit',
  'Tenant Status'
];
const RUBS_NEW_CHARGES_HEADER_ROW = [...RUBS_XLS_HEADER_ROW,'Final Bill Date'];
const BILLS_IMPORT_HEADER_ROW = [
  'Vendor','Portfolio','Building','Unit',
  'Date','Amount','Account','Split Comments',
  'Bill Description','Ref #','Payment Account','Payment Type',
  'Payment Check No','Payment Comments','Work Order #'
];
const RUBS_DIFF_HEADER_ROW = [
  'Lease','Account','ORIG AMOUNT','ORIG COMMENT','NEW AMOUNT','NEW COMMENT'
];

const hasData = arr => arr && arr.length > 0;

const convertToSheetJSStream = (data, buildRowFn, wbTitle, headerRow) => {
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: wbTitle,
  };
  wb.SheetNames.push("data");
  const rows = [];
  rows.push(headerRow);
  for (const datum of data) {
    rows.push(buildRowFn(datum));
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);
  wb.Sheets["data"] = ws;
  return XLSX.write(wb, {bookType:'xlsx', type:'binary'});
};

const streamToArrayBuffer = (octetStream) => {
  const buffer = new ArrayBuffer(octetStream.length);
  const view = new Uint8Array(buffer);
  for (let i=0; i<octetStream.length; i++) {
    view[i] = octetStream.charCodeAt(i) & 0xFF;
  }
  return buffer;
};

const saveStream = (stream, fileName) => {
  const buffer = streamToArrayBuffer(stream);
  saveAs(new Blob([buffer],{type:"application/octet-stream"}), fileName);
}

const convertBillsImportToSheetJSStream = (billsImport, startDate, endDate) => {
  const convertBillsImportRecord = (billsImportRecord) => {
    const {
      Vendor:vendor,
      Portfolio:portfolio,
      Building:building,
      Unit:unit,
      Date:date,
      Amount:amount,
      Account:account,
      "Split Comments":splitComments,
      "Bill Description":billDescription,
      "Ref #":refNo,
    } = billsImportRecord;
    const roundedAmount = Math.round((amount+Number.EPSILON)*100)/100;
    return [vendor,portfolio,building,unit,date,roundedAmount,account,splitComments
      ,billDescription
      ,refNo
      ,// Payment Account
      ,// Payment Type
      ,// Payment Check No
      ,// Payment Comments
      ,// Work Order #
    ];
  };
  const wbTitle = "RUBS Bills " + startDate + " to " + endDate;
  return convertToSheetJSStream(billsImport, convertBillsImportRecord, wbTitle, BILLS_IMPORT_HEADER_ROW);
};

const convertRUBSDataToSheetJSStream = (tenantTotalCostSummary, startDate, endDate,) => {
  let chargeDate = new Date();
  chargeDate.setHours(12);
  const [year,month,day] = chargeDate.toISOString().split('T')[0].split('-');
  chargeDate = month + "/" + day + "/" + year;
  const convertTenantRecord = (tenantRecord) => {
    const {leaseName,amount,pwAccount,comment,tenantStatus} = tenantRecord;
    const roundedAmount = Math.round((amount+Number.EPSILON)*100)/100;
    return [leaseName,chargeDate,roundedAmount,pwAccount,comment,,,,,,tenantStatus];
  };
  const wbTitle = "RUBS Charges " + startDate + " to " + endDate;
  return convertToSheetJSStream(tenantTotalCostSummary, convertTenantRecord, wbTitle, RUBS_XLS_HEADER_ROW);
};

const convertRUBSNewDataToSheetJSStream = (tenantTotalCostSummary, startDate, endDate,) => {
  let chargeDate = new Date();
  chargeDate.setHours(12);
  const [year,month,day] = chargeDate.toISOString().split('T')[0].split('-');
  chargeDate = month + "/" + day + "/" + year;
  const convertTenantRecord = (tenantRecord) => {
    const {leaseName,amount,pwAccount,comment,tenantStatus,finalBillDate} = tenantRecord;
    const roundedAmount = Math.round((amount+Number.EPSILON)*100)/100;
    return [leaseName,chargeDate,roundedAmount,pwAccount,comment,,,,,,tenantStatus,finalBillDate];
  };
  const wbTitle = "RUBS New Charges " + startDate + " to " + endDate;
  return convertToSheetJSStream(tenantTotalCostSummary, convertTenantRecord, wbTitle, RUBS_NEW_CHARGES_HEADER_ROW);
};

const convertDiffsDataToSheetJsStream = (diffs, startDate, endDate) => {
  const convertRecord = (record) => {
    const {leaseName,pwAccount,origAmount,origComment,newAmount,newComment} = record;
    const roundedOrigAmount = Math.round((origAmount+Number.EPSILON)*100)/100;
    const roundedNewAmount = Math.round((newAmount+Number.EPSILON)*100)/100;
    return [leaseName,pwAccount,roundedOrigAmount,origComment,roundedNewAmount,newComment];
  };
  const wbTitle = "RUBS Diffs from Prior Export " + startDate + " to " + endDate;
  return convertToSheetJSStream(diffs, convertRecord, wbTitle, RUBS_DIFF_HEADER_ROW);
};

/*
 * NOTE: EXPORT_VERSION_STAMP, EXPORT_TYPE, and EXPORT_VERSION
 *       are duplicated in lib/server/src/dao/etc/Exports.js
 */
const EXPORT_VERSION_STAMP = Object.freeze({
  "20241104": "20241104"
});
const EXPORT_TYPE = Object.freeze({
  UTILITIES: 'UTILITIES',
  INACTIVE_TENANTS: 'INACTIVE_TENANTS',
  BILLS_IMPORT: 'BILLS_IMPORT',
  RERUN_UTILITIES: 'RERUN_UTILITIES',
  RERUN_DIFFS: 'RERUN_DIFFS',
  NEW_CHARGES: 'NEW_CHARGES',
});
const EXPORT_VERSION = Object.freeze({
  UTILITIES: EXPORT_VERSION_STAMP["20241104"],
  INACTIVE_TENANTS: EXPORT_VERSION_STAMP["20241104"],
  BILLS_IMPORT: EXPORT_VERSION_STAMP["20241104"],
  RERUN_UTILITIES: EXPORT_VERSION_STAMP["20241104"],
  RERUN_DIFFS: EXPORT_VERSION_STAMP["20241104"],
  NEW_CHARGES: EXPORT_VERSION_STAMP["20241104"],
});


const EXPORT_PREFIX = Object.freeze({
  UTILITIES: 'RUBS',
  INACTIVE_TENANTS: 'RUBS_InactiveTenants',
  BILLS_IMPORT: 'RUBS_Bills_Import',
  RERUN_UTILITIES: 'RUBS_Rerun',
  RERUN_DIFFS: 'RUBS_Rerun_Diffs',
  NEW_CHARGES: 'RUBS_Rerun_New_Charges',
});

const getExportFileName = (exportType, startDate, endDate) => {
  const prefix = EXPORT_PREFIX[exportType];
  if (!prefix) {
    throw Error("Invalid exportType: " + exportType);
  }
  return prefix + "_" + startDate.replaceAll('-','')
    + "_" + endDate.replaceAll('-','') + ".xlsx";
};

const generateExport = (data, exportType, startDate, endDate, convertFunc) => {
  if (hasData(data)) {
    const filename = getExportFileName(exportType, startDate, endDate);
    const stream = convertFunc(data, startDate, endDate);
    saveStream(stream, filename);
  }
}
const filterInvalidRows = el => {
  return el.leaseName && el.amount && el.amount > 0;
}
const generateRUBSExport = (data, startDate, endDate, version=EXPORT_VERSION_STAMP["20241104"]) => {
  if (version === EXPORT_VERSION_STAMP["20241104"]) {
    const filteredData = data.filter(filterInvalidRows);
    generateExport(filteredData, EXPORT_TYPE.UTILITIES, startDate, endDate, convertRUBSDataToSheetJSStream);
  } else {
    throw Error("Invalid version: " + version);
  }
}
const generateRerunExport = (data, startDate, endDate, version=EXPORT_VERSION_STAMP["20241104"]) => {
  if (version === EXPORT_VERSION_STAMP["20241104"]) {
    generateExport(data, EXPORT_TYPE.RERUN_UTILITIES, startDate, endDate, convertRUBSDataToSheetJSStream);
  } else {
    throw Error("Invalid version: " + version);
  }
}
const generateRerunDiffs = (data, startDate, endDate, version=EXPORT_VERSION_STAMP["20241104"]) => {
  if (version === EXPORT_VERSION_STAMP["20241104"]) {
    generateExport(data, EXPORT_TYPE.RERUN_DIFFS, startDate, endDate, convertDiffsDataToSheetJsStream);
  } else {
    throw Error("Invalid version: " + version);
  }
}
const generateRerunNewCharges = (data, startDate, endDate, version=EXPORT_VERSION_STAMP["20241104"]) => {
  if (version === EXPORT_VERSION_STAMP["20241104"]) {
    generateExport(data, EXPORT_TYPE.NEW_CHARGES, startDate, endDate, convertRUBSNewDataToSheetJSStream);
  } else {
    throw Error("Invalid version: " + version);
  }
}
const generateInactiveTenantsExport = (data, startDate, endDate, version=EXPORT_VERSION_STAMP["20241104"]) => {
  if (version === EXPORT_VERSION_STAMP["20241104"]) {
    const filteredData = data.filter(filterInvalidRows);
    generateExport(filteredData, EXPORT_TYPE.INACTIVE_TENANTS, startDate, endDate, convertRUBSDataToSheetJSStream);
  } else {
    throw Error("Invalid version: " + version);
  }
};
const generateBillsImport = (data, startDate, endDate, version=EXPORT_VERSION_STAMP["20241104"]) => {
  if (version === EXPORT_VERSION_STAMP["20241104"]) {
    generateExport(data, EXPORT_TYPE.BILLS_IMPORT, startDate, endDate, convertBillsImportToSheetJSStream);
  } else {
    throw Error("Invalid version: " + version);
  }
}



export {
  EXPORT_TYPE,
  getExportFileName,
  generateRUBSExport,
  generateInactiveTenantsExport,
  generateBillsImport,
  generateRerunExport,
  generateRerunDiffs,
  generateRerunNewCharges,
}